import { Link } from "react-router-dom";
import {
  Activity,
  Compass,
  MapPin,
  ShieldCheck,
  ArrowRight,
  Gauge,
  AlertTriangle,
} from "lucide-react";

const FEATURES = [
  {
    icon: Activity,
    title: "Accelerometer Tracking",
    description: "Real-time XYZ acceleration data captures riding smoothness, braking patterns, and overall vehicle handling.",
    color: "text-yellow-500",
    bgColor: "bg-yellow-500/10",
    borderColor: "border-yellow-500/20",
  },
  {
    icon: Compass,
    title: "Magnetometer Analysis",
    description: "Precise heading and orientation data monitors turning consistency and directional control.",
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/20",
  },
  {
    icon: MapPin,
    title: "GPS Route Tracking",
    description: "Live speed and position monitoring ensures full route completion within the designated test area.",
    color: "text-sky-400",
    bgColor: "bg-sky-500/10",
    borderColor: "border-sky-500/20",
  },
  {
    icon: Gauge,
    title: "Speed Analysis",
    description: "Continuous speed monitoring checks adherence to posted limits throughout the driving test course.",
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/20",
  },
  {
    icon: AlertTriangle,
    title: "Harsh Event Detection",
    description: "Sudden braking, aggressive acceleration, and sharp turns are flagged as harsh movement events.",
    color: "text-red-400",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/20",
  },
  {
    icon: ShieldCheck,
    title: "Automated Scoring",
    description: "A transparent rules engine evaluates route, speed, stability, and harsh events for unbiased results.",
    color: "text-violet-400",
    bgColor: "bg-violet-500/10",
    borderColor: "border-violet-500/20",
  },
];

const PROCESS_STEPS = [
  { step: "01", title: "Mount Sensor", description: "IoT sensor device is attached to the 2-wheeler before the test begins." },
  { step: "02", title: "Drive Test Route", description: "Candidate completes the designated RTO route while sensors collect live data." },
  { step: "03", title: "Auto Evaluation", description: "The rules engine scores the ride on route, speed, stability, and movement metrics." },
  { step: "04", title: "Instant Result", description: "Pass or Fail result is displayed immediately. Passed candidates get a digital certificate." },
];

export function ProcessSection() {
  return (
    <section data-testid="how-it-works" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <span className="text-xs uppercase tracking-[0.2em] font-mono text-yellow-500 mb-3 block">
            Process
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white">
            How the Smart Test Works
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROCESS_STEPS.map((item, i) => (
            <div
              key={item.step}
              className="relative bg-zinc-900 border border-white/10 p-6 rounded-xl hover:border-yellow-500/30 transition-colors duration-300 group"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <span className="text-5xl font-heading font-black text-white/5 absolute top-4 right-4 group-hover:text-yellow-500/10 transition-colors">
                {item.step}
              </span>
              <div className="relative">
                <h3 className="font-heading text-lg font-semibold text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FeaturesSection() {
  return (
    <section data-testid="features-grid" className="py-20 sm:py-28 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <span className="text-xs uppercase tracking-[0.2em] font-mono text-yellow-500 mb-3 block">
            Sensor Metrics
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white">
            What We Measure
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="bg-zinc-900 border border-white/10 p-6 rounded-xl hover:border-yellow-500/30 transition-all duration-300 group"
              >
                <div className={`w-10 h-10 rounded-lg ${feature.bgColor} border ${feature.borderColor} flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 ${feature.color}`} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function CTASection() {
  return (
    <section className="py-20 sm:py-28 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white mb-4">
          Ready to Experience Smart Testing?
        </h2>
        <p className="text-zinc-400 max-w-lg mx-auto mb-8">
          See how sensor-based evaluation works with our live simulation demo.
        </p>
        <Link
          to="/test"
          data-testid="cta-start-test"
          className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-all duration-200 hover:shadow-lg hover:shadow-yellow-500/20"
        >
          Run Test Simulation
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

export function FooterSection() {
  return (
    <footer className="border-t border-white/5 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-yellow-500" />
          <span className="text-sm font-mono text-zinc-500">SecureRTO v1.0 Prototype</span>
        </div>
        <p className="text-xs text-zinc-600 font-mono">
          Mock Data Only - No Real Sensor Integration
        </p>
      </div>
    </footer>
  );
}
